<template>
  <div class="autocomplete-wrap" v-click-outside="closeDropdown">
    <div class="form-floating">
      <input
        ref="input"
        :value="searchQuery"
        @input="onInput($event.target.value)"
        @focus="openDropdown"
        @click="openOnClick"
        @keydown.down.prevent="highlightNext"
        @keydown.up.prevent="highlightPrev"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.escape="closeDropdown"
        @keydown.tab="closeDropdown"
        type="text"
        class="form-control"
        :class="{ 'input-text-hidden': showSelectedOverlay }"
        :placeholder="label"
        autocomplete="off"
      />
      <label>
        <slot name="label">{{ label }}</slot>
      </label>
      <!-- Optional rich display of the selected item (e.g. with icons/badges), shown as an
           overlay while the dropdown is closed. The input keeps its text value for the
           floating-label state, but renders it transparent so it doesn't double up. -->
      <div v-if="showSelectedOverlay" class="autocomplete-selected-overlay">
        <slot name="selected" :item="modelValue"></slot>
      </div>
    </div>
    <div v-if="dropdownOpen && filtered.length > 0" class="autocomplete-dropdown">
      <div
        v-for="(item, index) in filtered"
        :key="itemKey(item)"
        class="autocomplete-option"
        :class="{ 'is-highlighted': index === highlightedIndex }"
        @mousedown.prevent="selectItem(item)"
        @mouseenter="highlightedIndex = index"
      >
        <slot name="option" :item="item">{{ itemLabel(item) }}</slot>
      </div>
    </div>
    <div v-if="dropdownOpen && searchQuery && filtered.length === 0" class="autocomplete-dropdown">
      <div class="autocomplete-option no-results">No results found</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SingleSelectAutocomplete',
  directives: {
    'click-outside': {
      mounted (el, binding) {
        el._clickOutside = (event) => {
          if (!el.contains(event.target)) {
            binding.value()
          }
        }
        document.addEventListener('mousedown', el._clickOutside)
      },
      unmounted (el) {
        document.removeEventListener('mousedown', el._clickOutside)
      }
    }
  },
  props: {
    modelValue: {
      required: false,
      default: null
    },
    options: {
      required: true,
      type: Array
    },
    label: {
      required: false,
      type: String,
      default: 'Select...'
    },
    optionLabel: {
      required: true,
      type: Function // (item) => 'display string'
    },
    optionKey: {
      required: true,
      type: Function // (item) => 'unique key'
    },
    filterFn: {
      required: false,
      type: Function, // (item, query) => boolean
      default: null
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      searchQuery: '',
      dropdownOpen: false,
      highlightedIndex: -1
    }
  },
  computed: {
    showSelectedOverlay () {
      return !!(this.$slots.selected && this.modelValue && !this.dropdownOpen)
    },
    filtered () {
      if (!this.searchQuery) return this.options
      const q = this.searchQuery.toLowerCase()
      if (this.filterFn) {
        return this.options.filter(item => this.filterFn(item, q))
      }
      return this.options
        .filter(item => this.itemLabel(item).toLowerCase().includes(q))
        .sort((a, b) => {
          const aStarts = this.itemLabel(a).toLowerCase().startsWith(q) ? 0 : 1
          const bStarts = this.itemLabel(b).toLowerCase().startsWith(q) ? 0 : 1
          return aStarts - bStarts
        })
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler (val) {
        this.searchQuery = val ? this.itemLabel(val) : ''
      }
    }
  },
  methods: {
    itemLabel (item) {
      return this.optionLabel(item)
    },
    itemKey (item) {
      return this.optionKey(item)
    },
    openDropdown () {
      this.dropdownOpen = true
      this.highlightedIndex = -1
      if (this.modelValue && this.searchQuery === this.itemLabel(this.modelValue)) {
        this.searchQuery = ''
      }
    },
    // Reopen the dropdown when clicking an already-focused input (focus alone won't
    // re-fire), but do NOTHING while it's already open — otherwise a click mid-typing
    // would reset the keyboard highlight and break Enter-to-select.
    openOnClick () {
      if (!this.dropdownOpen) this.openDropdown()
    },
    closeDropdown () {
      this.dropdownOpen = false
      this.highlightedIndex = -1
      this.searchQuery = this.modelValue ? this.itemLabel(this.modelValue) : ''
    },
    onInput (value) {
      this.searchQuery = value
      this.dropdownOpen = true
      this.highlightedIndex = 0
    },
    highlightNext () {
      if (this.filtered.length === 0) return
      this.highlightedIndex = (this.highlightedIndex + 1) % this.filtered.length
      this.scrollToHighlighted()
    },
    highlightPrev () {
      if (this.filtered.length === 0) return
      this.highlightedIndex = this.highlightedIndex <= 0
        ? this.filtered.length - 1
        : this.highlightedIndex - 1
      this.scrollToHighlighted()
    },
    selectHighlighted () {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filtered.length) {
        this.selectItem(this.filtered[this.highlightedIndex])
      }
    },
    selectItem (item) {
      this.searchQuery = this.itemLabel(item)
      this.dropdownOpen = false
      this.highlightedIndex = -1
      this.$emit('update:modelValue', item)
    },
    scrollToHighlighted () {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.autocomplete-dropdown')
        const highlighted = container?.querySelector('.is-highlighted')
        if (highlighted && container) {
          highlighted.scrollIntoView({ block: 'nearest' })
        }
      })
    }
  }
}
</script>

<style scoped>
.autocomplete-wrap {
  position: relative;
}
/* Aligned to the .form-floating filled-state text area (padding-top 1.625rem,
   padding-bottom .625rem; horizontal 1rem = the Falcon theme's .form-control
   padding-x, NOT Bootstrap's .75rem default). pointer-events: none lets clicks
   fall through to the input underneath. */
.autocomplete-selected-overlay {
  position: absolute;
  left: 1rem;
  right: 1rem;
  top: 1.625rem;
  bottom: 0.625rem;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  pointer-events: none;
}
.form-control.input-text-hidden {
  color: transparent !important;
}
.autocomplete-dropdown {
  position: absolute;
  z-index: 1050;
  width: 100%;
  max-height: 280px;
  overflow-y: auto;
  background-color: var(--falcon-input-bg, var(--falcon-quaternary-bg));
  border: 1px solid var(--falcon-input-border-color, var(--falcon-gray-300));
  border-top: 0;
  border-radius: 0 0 0.25rem 0.25rem;
  box-shadow: var(--falcon-box-shadow);
}
.autocomplete-option {
  padding: 0.4rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--falcon-body-color);
}
.autocomplete-option.is-highlighted {
  background-color: var(--falcon-primary);
  color: #fff;
}
.autocomplete-option.no-results {
  cursor: default;
  opacity: 0.6;
  font-style: italic;
}
</style>
